// Screen control for the rest timer — the "don't sleep while I'm resting" half.
//
// Two capabilities, deliberately split:
//   keepAwake() works everywhere it can — the Android shell's ScreenAwake plugin
//     (FLAG_KEEP_SCREEN_ON) natively, the Screen Wake Lock API on the PWA.
//   setAod() only exists natively: dimming the backlight and hiding the system bars are window
//     properties no web API exposes.
//
// Absent on plain web and on APKs built before the plugin shipped, so every call is a safe no-op
// there. `available()` gates the black AOD face itself — without the window controls a full-screen
// black overlay would just hide the app until the screen slept anyway.

function plugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.ScreenAwake) || null;
}

/** True only when the native window controls are callable. */
export function available() {
  return !!plugin();
}

let sentinel = null; // live WakeLockSentinel on the PWA path
let wanted = false; // what we last asked for — the re-acquire below needs it

// The browser drops a wake lock whenever the page hides, and never gives it back on its own. Ask
// again on return so a rest that outlives a glance at another app still holds the screen.
function watchVisibility() {
  if (watchVisibility.installed) return;
  watchVisibility.installed = true;
  document.addEventListener("visibilitychange", () => {
    if (wanted && document.visibilityState === "visible") void acquireWakeLock();
  });
}

let acquiring = false;

async function acquireWakeLock() {
  if (sentinel || acquiring || !navigator.wakeLock) return;
  acquiring = true;
  try {
    const held = await navigator.wakeLock.request("screen");
    // A rest that ended (or a tab that hid) while the request was in flight must not leave a lock
    // behind — nothing would ever release it.
    if (!wanted) { void held.release(); return; }
    sentinel = held;
    held.addEventListener("release", () => { if (sentinel === held) sentinel = null; });
  } catch { /* denied, unsupported, or the page went hidden mid-request */
  } finally { acquiring = false; }
}

/** Hold the screen on (or let it sleep again). Safe to call repeatedly. */
export async function keepAwake(on) {
  wanted = !!on;
  const p = plugin();
  if (p) {
    try { await p.keepAwake({ on: wanted }); } catch { /* best effort */ }
    return;
  }
  if (!wanted) {
    const held = sentinel;
    sentinel = null; // cleared first, so a re-acquire racing this release isn't skipped
    try { await held?.release(); } catch { /* already gone */ }
    return;
  }
  watchVisibility();
  await acquireWakeLock();
}

/** Enter/leave the dimmed, bar-free window the black countdown is drawn on. Native only. */
export async function setAod(on, brightness = 0.08) {
  const p = plugin();
  if (!p) return;
  try { await p.setAod({ on: !!on, brightness }); } catch { /* best effort */ }
}
